import { Component, computed, inject, signal } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { Router } from '@angular/router';
import { AlertaService } from '../../../../shared/utils/alerta.service';
import { cpfValido } from '../../../../shared/utils/documento.util';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { LucideModule } from '../../../../shared/modules/lucide.module';

interface CadastroCidadao {
  nomeCompleto: string;
  nomeSocial: string;
  genero: string;
  racaCor: string;
  dataNascimento: string;
  rg: string;
  cpf: string;
  dependentes: string;

  enderecoCompleto: string;
  numero: string;
  complemento: string;
  cep: string;
  bairro: string;
  estado: string;
  cidade: string;

  email: string;
  senha: string;
  telefoneResidencial: string;
  telefoneCelular01: string;
  telefoneCelular02: string;
}

type CampoCadastro = keyof CadastroCidadao;

const CAMPOS_POR_ETAPA: readonly CampoCadastro[][] = [
  ['nomeCompleto', 'genero', 'racaCor', 'dataNascimento', 'rg', 'cpf'],
  ['enderecoCompleto', 'numero', 'cep', 'bairro', 'estado', 'cidade'],
  ['email', 'senha', 'telefoneCelular01'],
];

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Mínimo de 8 caracteres, com pelo menos uma letra e um número. */
const SENHA_SEGURA = /^(?=.*[A-Za-zÀ-ÿ])(?=.*\d).{8,}$/;

const VALOR_INICIAL: CadastroCidadao = {
  nomeCompleto: '',
  nomeSocial: '',
  genero: '',
  racaCor: '',
  dataNascimento: '',
  rg: '',
  cpf: '',
  dependentes: '',
  enderecoCompleto: '',
  numero: '',
  complemento: '',
  cep: '',
  bairro: '',
  estado: '',
  cidade: '',
  email: '',
  senha: '',
  telefoneResidencial: '',
  telefoneCelular01: '',
  telefoneCelular02: '',
};

@Component({
  selector: 'app-cadastro',
  templateUrl: './cadastro.component.html',
  styleUrls: ['./cadastro.component.css'],
  imports: [FormField, LucideModule, BreadcrumbComponent],
})
export class CadastroComponent {
  private readonly router = inject(Router);
  private readonly alerta = inject(AlertaService);

  readonly dados = signal<CadastroCidadao>({ ...VALOR_INICIAL });
  readonly cadastroForm = form(this.dados);

  readonly generos = ['Feminino', 'Masculino', 'Não binário', 'Prefiro não informar'];
  readonly racasCores = ['Branca', 'Preta', 'Parda', 'Amarela', 'Indígena', 'Não declarada'];
  readonly estados = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
    'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
  ];

  readonly cidades = computed(() => (this.dados().estado ? ['Salvador'] : []));

  readonly progressoEtapas = computed(() => {
    const valores = this.dados();

    return CAMPOS_POR_ETAPA.map((campos) => {
      const preenchidos = campos.filter((campo) => valores[campo].trim() !== '').length;
      return Math.round((preenchidos / campos.length) * 100);
    });
  });

  /** Indica que a pessoa já começou a preencher — usado só para exibir as pendências. */
  readonly preenchido = computed(() =>
    Object.values(this.dados()).some((valor) => valor.trim() !== '')
  );

  /**
   * O envio só é liberado com todos os campos obrigatórios preenchidos e com CPF, e-mail e senha
   * válidos: a mesma checagem precisa existir no servidor, aqui ela evita cadastros inconsistentes.
   */
  readonly pendencias = computed<readonly string[]>(() => {
    const valores = this.dados();
    const pendencias: string[] = [];

    if (CAMPOS_POR_ETAPA.flat().some((campo) => valores[campo].trim() === '')) {
      pendencias.push('Preencha todos os campos obrigatórios das três etapas.');
    }

    if (!cpfValido(valores.cpf)) {
      pendencias.push('Informe um CPF válido.');
    }

    if (!EMAIL.test(valores.email.trim())) {
      pendencias.push('Informe um e-mail válido.');
    }

    if (!SENHA_SEGURA.test(valores.senha)) {
      pendencias.push('A senha precisa ter ao menos 8 caracteres, com letras e números.');
    }

    return pendencias;
  });

  readonly podeEnviar = computed(() => this.pendencias().length === 0);

  readonly progressoTotal = computed(() => {
    const valores = this.dados();
    const campos = CAMPOS_POR_ETAPA.flat();
    const preenchidos = campos.filter((campo) => valores[campo].trim() !== '').length;

    return Math.round((preenchidos / campos.length) * 100);
  });

  async continuar(evento: Event): Promise<void> {
    evento.preventDefault();

    if (!this.podeEnviar()) {
      return;
    }

    await this.alerta.sucesso({
      titulo: 'Cadastro realizado!',
      texto:
        'Enviamos um e-mail de confirmação para o endereço informado. Você já pode acessar a área do cidadão.',
      confirmar: 'Ir para a área do cidadão',
    });

    this.router.navigate(['/area-cidadao']);
  }

  voltar(): void {
    history.back();
  }
}
